/**
 * Resaltado de los términos buscados, sin HTML: devuelve tramos de texto que el componente `Resaltado` dibuja como
 * nodos de React. Reemplaza a `highlightSearchTerms` (armaba `<mark>` en un string que se pintaba con
 * `dangerouslySetInnerHTML`: un título o resumen con `<img onerror>` se ejecutaba; salud de código 15-10, punto 5).
 *
 * Insensible a mayúsculas y a tildes («resolucion» resalta «Resolución»): se busca sobre una copia sin diacríticos
 * del mismo largo y se corta el texto original por las mismas posiciones.
 */

export interface Tramo {
  texto: string;
  resaltado: boolean;
}

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Quita diacríticos carácter a carácter, sin cambiar el largo (las posiciones siguen valiendo para el original). */
const plegar = (s: string) =>
  Array.from(s, c => (c.length === 1 ? c.normalize('NFD').charAt(0) : c)).join('').toLowerCase();

/** Términos de la consulta (sin repetidos, de 2+ caracteres), del más largo al más corto. */
export function terminos(query: string, minTermLength = 2): string[] {
  const limpios = query
    .trim()
    .split(/\s+/)
    .map(t => plegar(t.replace(/^["'«(]+|["'»),.;:]+$/g, '')))
    .filter(t => t.length >= minTermLength);
  return Array.from(new Set(limpios)).sort((a, b) => b.length - a.length);
}

/**
 * Corta `text` en tramos normales y resaltados según los términos de `query`. Sin consulta, un solo tramo.
 * Con `frase`, la consulta entera es un solo término (el buscador dentro de un documento busca «corrección monetaria»,
 * no cada palabra por separado); los espacios de la consulta calzan con cualquier espacio del texto.
 */
export function resaltarTramos(text: string, query: string, minTermLength = 2, frase = false): Tramo[] {
  if (!text) return [];
  const unaFrase = plegar(query.trim().replace(/\s+/g, ' '));
  const terms = !query ? [] : frase ? (unaFrase.length >= minTermLength ? [unaFrase] : []) : terminos(query, minTermLength);
  if (terms.length === 0) return [{ texto: text, resaltado: false }];
  // Salvaguarda: si el plegado cambiara el largo, las posiciones no servirían; se muestra sin resaltar.
  const plegado = plegar(text);
  if (plegado.length !== text.length) return [{ texto: text, resaltado: false }];
  const re = new RegExp(terms.map(t => escapeRegex(t).replace(/ /g, '\\s+')).join('|'), 'g');
  const tramos: Tramo[] = [];
  let ultimo = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(plegado)) !== null) {
    if (m[0].length === 0) { re.lastIndex++; continue; }
    if (m.index > ultimo) tramos.push({ texto: text.slice(ultimo, m.index), resaltado: false });
    tramos.push({ texto: text.slice(m.index, m.index + m[0].length), resaltado: true });
    ultimo = m.index + m[0].length;
  }
  if (ultimo < text.length) tramos.push({ texto: text.slice(ultimo), resaltado: false });
  return tramos;
}

/**
 * Extracto de `text` alrededor de la primera aparición de un término de `query` (mismo criterio que el resaltado:
 * sin tildes ni mayúsculas, términos de 2+ caracteres). Sin coincidencia, el inicio del texto.
 * `coincide` dice si el extracto contiene la consulta (para rotularlo con honestidad).
 */
export function extracto(text: string | null | undefined, query: string, max = 460): { texto: string; coincide: boolean } {
  if (!text) return { texto: '', coincide: false };
  const cortar = (desde: number) => {
    const fin = Math.min(text.length, desde + max);
    return `${desde > 0 ? '…' : ''}${text.slice(desde, fin)}${fin < text.length ? '…' : ''}`;
  };
  const terms = query ? terminos(query) : [];
  const plegado = plegar(text);
  let primero = -1;
  if (plegado.length === text.length) {
    for (const t of terms) {
      const i = plegado.indexOf(t);
      if (i !== -1 && (primero === -1 || i < primero)) primero = i;
    }
  }
  if (primero === -1) return { texto: cortar(0), coincide: false };
  return { texto: cortar(Math.max(0, primero - 60)), coincide: true };
}

/** Cuántas veces aparecen los términos de `query` en `text`. */
export function countMatches(text: string, query: string): number {
  return resaltarTramos(text, query).filter(t => t.resaltado).length;
}
